import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2, Phone, Mail, MessageCircle, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { submitEnquiry } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { MONTHLY_PLANS } from '../data/content';

const TRADE_OPTIONS = [
  ['painter-decorator', 'Painter & Decorator'],
  ['builder', 'Builder / General Contractor'],
  ['plumber', 'Plumber'],
  ['electrician', 'Electrician'],
  ['landscaper', 'Landscaper / Gardener'],
  ['roofer', 'Roofer'],
  ['plasterer', 'Plasterer'],
  ['carpenter-joiner', 'Carpenter / Joiner'],
  ['handyman', 'Handyman'],
  ['other', 'Another trade'],
];

const PRESENCE_OPTIONS = [
  ['nothing', 'I have nothing set up yet'],
  ['google-only', 'I have a Google Business Profile only'],
  ['social-no-website', 'I have social media but no website'],
  ['website-needs-work', 'I have a website but need improvements'],
  ['improve-existing', 'I already have some online presence and want help improving it'],
];

const PACKAGE_OPTIONS = [
  ['get-found', 'Get Found — £249'],
  ['look-professional', 'Look Professional — £599'],
  ['built-to-grow', 'Built to Grow — £899'],
  ['monthly-support', 'Monthly support'],
  ['not-sure', 'Not sure — please advise'],
];

const CONTACT_OPTIONS = [
  ['phone', 'Phone call', Phone],
  ['email', 'Email', Mail],
  ['whatsapp', 'WhatsApp', MessageCircle],
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const initial = {
  full_name: '',
  business_name: '',
  email: '',
  phone: '',
  trade_type: '',
  online_presence: '',
  package_interest: '',
  preferred_contact: '',
  project_details: '',
  marketing_consent: false,
  company_website: '',
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => {
    const pkg = searchParams.get('package') || '';
    const monthly = MONTHLY_PLANS.find((p) => p.slug === pkg);
    const next = { ...initial };
    if (monthly) {
      next.package_interest = 'monthly-support';
      next.project_details = `I'm interested in the ${monthly.name} plan (${monthly.price} ${monthly.period}). `;
    } else if (PACKAGE_OPTIONS.some(([v]) => v === pkg)) {
      next.package_interest = pkg;
    }
    return next;
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const selectedPackageLabel = useMemo(
    () => PACKAGE_OPTIONS.find(([v]) => v === form.package_interest)?.[1],
    [form.package_interest]
  );

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = 'Please enter your full name.';
    if (!EMAIL_RE.test(form.email.trim())) er.email = 'Please enter a valid email address, for example dave@example.co.uk.';
    if (!form.trade_type) er.trade_type = 'Please choose your trade.';
    if (!form.online_presence) er.online_presence = 'Please choose the option closest to your situation.';
    if (!form.package_interest) er.package_interest = 'Please choose a package — “Not sure” is fine.';
    if (!form.preferred_contact) er.preferred_contact = 'Please tell us how you’d like to be contacted.';
    if (form.project_details.trim().length < 10)
      er.project_details = 'Tell us a little about your business — a sentence or two is plenty.';
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length > 0) {
      const first = document.querySelector('[aria-invalid="true"]');
      first?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitEnquiry({ ...form, email: form.email.trim() });
      setSubmitted(res);
      trackEvent('enquiry_submitted', { package: form.package_interest });
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 429
          ? 'You’ve sent a few enquiries already. Please wait a little while and try again.'
          : 'Something went wrong sending your enquiry. Please try again in a moment.';
      setErrors((p) => ({ ...p, form: msg }));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (key) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-base text-charcoal placeholder:text-stonebrand/60 transition-[border-color,box-shadow] duration-200 focus-visible:outline-none ${
      errors[key] ? 'border-red-700' : 'border-hairline hover:border-charcoal/30 focus:border-charcoal'
    }`;

  const ErrorText = ({ id, children }) =>
    children ? (
      <p id={id} role="alert" className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-red-700">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        {children}
      </p>
    ) : null;

  const Label = ({ htmlFor, children, optional }) => (
    <label htmlFor={htmlFor} className="mb-1.5 block font-heading text-sm font-bold tracking-tight text-charcoal">
      {children}
      {optional && <span className="ml-2 text-xs font-normal text-stonebrand">(optional)</span>}
    </label>
  );

  return (
    <>
      <Seo
        title="Get a Free Quote — Contact Build My Trade"
        description="Tell us about your trade business and get a free, no-obligation quote. Websites, Google Business Profiles and social media setup for UK tradespeople."
        path="/contact"
      />
      <PageHero
        eyebrow="Free quote"
        title="Tell us about your business"
        intro="A few straightforward questions — no commitment, no sales patter. We’ll reply to talk it through and confirm exactly what’s included before you pay anything."
        crumbs={[{ label: 'Contact' }]}
        testid="contact-heading"
      />

      <section className="bg-offwhite" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              {submitted ? (
                <Reveal>
                  <div
                    className="rounded-2xl border border-hairline bg-white p-8 sm:p-10"
                    data-testid="enquiry-success"
                    role="status"
                  >
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-lime">
                      <CheckCircle2 className="h-7 w-7 text-charcoal" aria-hidden="true" />
                    </span>
                    <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-charcoal sm:text-3xl">
                      Thanks{form.full_name ? `, ${form.full_name.split(' ')[0]}` : ''} — your enquiry is in.
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-stonebrand">
                      We’ve received your details{selectedPackageLabel ? ` and your interest in ` : ''}
                      {selectedPackageLabel && <span className="font-semibold text-charcoal">{selectedPackageLabel}</span>}.
                      We’ll be in touch using your preferred contact method — usually within one working day.
                    </p>
                    <p className="mt-4 rounded-xl bg-offwhite px-4 py-3 font-mono text-xs text-stonebrand">
                      Reference: {submitted.reference}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setForm(initial);
                        setSubmitted(null);
                      }}
                      data-testid="send-another-enquiry"
                      className="mt-6 text-sm font-semibold text-charcoal underline underline-offset-4 hover:text-charcoal/70"
                    >
                      Send another enquiry
                    </button>
                  </div>
                </Reveal>
              ) : (
                <Reveal>
                  <form
                    onSubmit={onSubmit}
                    noValidate
                    className="rounded-2xl border border-hairline bg-white p-6 sm:p-10"
                    data-testid="enquiry-form"
                  >
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="full_name">Full name</Label>
                        <input
                          id="full_name"
                          type="text"
                          autoComplete="name"
                          value={form.full_name}
                          onChange={set('full_name')}
                          aria-invalid={Boolean(errors.full_name)}
                          aria-describedby={errors.full_name ? 'err-full_name' : undefined}
                          className={fieldClass('full_name')}
                          data-testid="input-full-name"
                        />
                        <ErrorText id="err-full_name">{errors.full_name}</ErrorText>
                      </div>
                      <div>
                        <Label htmlFor="business_name" optional>
                          Business name
                        </Label>
                        <input
                          id="business_name"
                          type="text"
                          autoComplete="organization"
                          value={form.business_name}
                          onChange={set('business_name')}
                          className={fieldClass('business_name')}
                          data-testid="input-business-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email address</Label>
                        <input
                          id="email"
                          type="email"
                          autoComplete="email"
                          value={form.email}
                          onChange={set('email')}
                          aria-invalid={Boolean(errors.email)}
                          aria-describedby={errors.email ? 'err-email' : undefined}
                          className={fieldClass('email')}
                          data-testid="input-email"
                        />
                        <ErrorText id="err-email">{errors.email}</ErrorText>
                      </div>
                      <div>
                        <Label htmlFor="phone" optional>
                          Phone number
                        </Label>
                        <input
                          id="phone"
                          type="tel"
                          autoComplete="tel"
                          value={form.phone}
                          onChange={set('phone')}
                          className={fieldClass('phone')}
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="trade_type">Type of trade</Label>
                        <select
                          id="trade_type"
                          value={form.trade_type}
                          onChange={set('trade_type')}
                          aria-invalid={Boolean(errors.trade_type)}
                          aria-describedby={errors.trade_type ? 'err-trade_type' : undefined}
                          className={fieldClass('trade_type')}
                          data-testid="select-trade-type"
                        >
                          <option value="">Choose your trade…</option>
                          {TRADE_OPTIONS.map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                        <ErrorText id="err-trade_type">{errors.trade_type}</ErrorText>
                      </div>
                      <div>
                        <Label htmlFor="online_presence">Current online presence</Label>
                        <select
                          id="online_presence"
                          value={form.online_presence}
                          onChange={set('online_presence')}
                          aria-invalid={Boolean(errors.online_presence)}
                          aria-describedby={errors.online_presence ? 'err-online_presence' : undefined}
                          className={fieldClass('online_presence')}
                          data-testid="select-online-presence"
                        >
                          <option value="">Choose the closest option…</option>
                          {PRESENCE_OPTIONS.map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                        <ErrorText id="err-online_presence">{errors.online_presence}</ErrorText>
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="package_interest">Package of interest</Label>
                        <select
                          id="package_interest"
                          value={form.package_interest}
                          onChange={set('package_interest')}
                          aria-invalid={Boolean(errors.package_interest)}
                          aria-describedby={errors.package_interest ? 'err-package_interest' : undefined}
                          className={fieldClass('package_interest')}
                          data-testid="select-package"
                        >
                          <option value="">Choose a package…</option>
                          {PACKAGE_OPTIONS.map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                        <ErrorText id="err-package_interest">{errors.package_interest}</ErrorText>
                      </div>
                      <fieldset className="sm:col-span-2">
                        <legend className="mb-2 font-heading text-sm font-bold tracking-tight text-charcoal">
                          How would you like us to contact you?
                        </legend>
                        <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Preferred contact method">
                          {CONTACT_OPTIONS.map(([value, label, Icon]) => (
                            <label
                              key={value}
                              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                                form.preferred_contact === value
                                  ? 'border-charcoal bg-charcoal text-offwhite'
                                  : 'border-hairline bg-white text-charcoal hover:border-charcoal/40'
                              }`}
                              data-testid={`contact-method-${value}`}
                            >
                              <input
                                type="radio"
                                name="preferred_contact"
                                value={value}
                                checked={form.preferred_contact === value}
                                onChange={set('preferred_contact')}
                                className="sr-only"
                              />
                              <Icon className="h-4 w-4" aria-hidden="true" />
                              {label}
                            </label>
                          ))}
                        </div>
                        <ErrorText id="err-preferred_contact">{errors.preferred_contact}</ErrorText>
                      </fieldset>
                      <div className="sm:col-span-2">
                        <Label htmlFor="project_details">Tell us about your business and what you need</Label>
                        <textarea
                          id="project_details"
                          rows={5}
                          value={form.project_details}
                          onChange={set('project_details')}
                          placeholder="For example: “I’m a self-employed plumber covering Leeds. I’ve been going three years, all word of mouth so far, and I need a website and somewhere customers can see my bathroom refits.”"
                          aria-invalid={Boolean(errors.project_details)}
                          aria-describedby={errors.project_details ? 'err-project_details' : undefined}
                          className={`${fieldClass('project_details')} resize-y`}
                          data-testid="input-project-details"
                        />
                        <ErrorText id="err-project_details">{errors.project_details}</ErrorText>
                      </div>
                      {/* Honeypot — invisible to humans, bots fill it in */}
                      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                        <label htmlFor="company_website">Company website</label>
                        <input
                          id="company_website"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          value={form.company_website}
                          onChange={set('company_website')}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-stonebrand">
                          <input
                            type="checkbox"
                            checked={form.marketing_consent}
                            onChange={set('marketing_consent')}
                            className="mt-1 h-4 w-4 shrink-0 accent-[#242721]"
                            data-testid="checkbox-marketing-consent"
                          />
                          <span>
                            <span className="font-semibold text-charcoal">Optional:</span> I’m happy for Build My
                            Trade to send me occasional useful updates about getting my business online. This is
                            completely optional and not needed to get a quote — see the{' '}
                            <a href="/privacy" className="underline underline-offset-2">
                              Privacy Policy
                            </a>
                            .
                          </span>
                        </label>
                      </div>
                    </div>

                    {errors.form && (
                      <p role="alert" className="mt-6 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="form-error">
                        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {errors.form}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="enquiry-submit"
                      className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-charcoal px-8 py-4 font-heading text-base font-bold text-offwhite transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-charcoal-surface disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                          Sending…
                        </>
                      ) : (
                        'Send My Enquiry'
                      )}
                    </button>
                    <p className="mt-4 text-xs leading-relaxed text-stonebrand">
                      Your details are used only to respond to your enquiry. They’re stored securely, never displayed
                      publicly and never sold. See our Privacy Policy for the full picture.
                    </p>
                  </form>
                </Reveal>
              )}
            </div>

            <aside className="lg:col-span-5">
              <div className="space-y-5 lg:sticky lg:top-28">
                <Reveal delay={0.1}>
                  <div className="rounded-2xl bg-charcoal p-8 text-offwhite" data-testid="contact-sidebar">
                    <h2 className="font-heading text-xl font-bold tracking-tight">What happens next</h2>
                    <ol className="mt-6 space-y-5">
                      {[
                        'We read your enquiry properly — no autoresponder waffle.',
                        'We get in touch your preferred way, usually within one working day.',
                        'We talk it through in plain English and confirm exactly what’s included.',
                        'You decide. There’s never any pressure and never any obligation.',
                      ].map((step, i) => (
                        <li key={i} className="flex gap-4">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-lime font-mono text-xs font-semibold text-charcoal">
                            {i + 1}
                          </span>
                          <p className="text-sm leading-relaxed text-offwhite/75">{step}</p>
                        </li>
                      ))}
                    </ol>
                  </div>
                </Reveal>
                <Reveal delay={0.18}>
                  <div className="rounded-2xl border border-hairline bg-white p-8">
                    <h2 className="font-heading text-lg font-bold tracking-tight text-charcoal">
                      Prefer to email directly?
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-stonebrand">
                      No problem — write to us any time and we’ll reply, usually within one working day.
                    </p>
                    <a
                      href="mailto:info@buildmytrade.uk"
                      data-testid="contact-email-link"
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-charcoal underline underline-offset-4 hover:text-charcoal/70"
                    >
                      <Mail className="h-4 w-4" aria-hidden="true" />
                      info@buildmytrade.uk
                    </a>
                    <p className="mt-4 border-t border-hairline pt-4 text-sm leading-relaxed text-stonebrand">
                      You can also follow or message us on{' '}
                      <a
                        href="https://www.instagram.com/buildmytradeuk"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-instagram-link"
                        className="inline-flex items-center gap-1 font-semibold text-charcoal underline underline-offset-4 hover:text-charcoal/70"
                      >
                        <Instagram className="h-4 w-4" aria-hidden="true" />
                        Instagram
                      </a>
                      .
                    </p>
                  </div>
                </Reveal>
                <Reveal delay={0.24}>
                  <div className="rounded-2xl border border-hairline bg-white p-8">
                    <h2 className="font-heading text-lg font-bold tracking-tight text-charcoal">
                      Prefer to just pick a package?
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-stonebrand">
                      All three packages have fixed published prices — take a look and come back with questions if
                      you have them.
                    </p>
                    <a
                      href="/pricing"
                      data-testid="contact-pricing-link"
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal underline underline-offset-4 hover:text-charcoal/70"
                    >
                      See packages and pricing
                    </a>
                  </div>
                </Reveal>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
